import { makeAutoObservable, runInAction } from 'mobx';
import type { FHRequest, FHRequestMessage } from '../types';
import type { CreateRequestParams } from '../api/request_api';
import { requestApi } from '../api/request_api';

export class RequestStore {
  // Request storage
  requests: Map<string, FHRequest> = new Map();
  requestsLoading = false;
  requestsCursor: string | null = null;
  requestsHasMore = true;
  // Which list the requests map holds: the default list or the archived one
  requestsArchived = false;

  // Messages storage (keyed by request uuid)
  messages: Map<string, FHRequestMessage[]> = new Map();
  messagesLoading = false;
  messagesCursors: Map<string, string | null> = new Map();
  messagesHasMore: Map<string, boolean> = new Map();

  // Current request being viewed
  currentRequestKey: string | null = null;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  get allRequests(): FHRequest[] {
    return Array.from(this.requests.values()).sort(
      (a, b) =>
        new Date(b.date_created).getTime() - new Date(a.date_created).getTime()
    );
  }

  get currentRequest(): FHRequest | undefined {
    return this.currentRequestKey
      ? this.requests.get(this.currentRequestKey)
      : undefined;
  }

  get currentMessages(): FHRequestMessage[] {
    if (!this.currentRequest?.key) return [];
    return this.messages.get(this.currentRequest.key) || [];
  }

  getRequestByKey(key: string): FHRequest | undefined {
    return this.requests.get(key);
  }

  // Actions
  setCurrentRequest(key: string | null): void {
    this.currentRequestKey = key;
  }

  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch user's requests
   */
  async fetchRequests(refresh = false, archived = this.requestsArchived): Promise<void> {
    // Switching lists always refreshes, even mid-load; the stale response is dropped below
    if (archived !== this.requestsArchived) refresh = true;
    else if (this.requestsLoading) return;
    if (!refresh && !this.requestsHasMore) return;

    runInAction(() => {
      // Don't show the other list's requests while this one loads
      if (archived !== this.requestsArchived) this.requests.clear();
      this.requestsLoading = true;
      this.requestsArchived = archived;
      this.error = null;
      if (refresh) {
        this.requestsCursor = null;
        this.requestsHasMore = true;
      }
    });

    try {
      const cursor = refresh ? undefined : this.requestsCursor || undefined;
      const response = archived
        ? await requestApi.listArchived(cursor)
        : await requestApi.listByUser(cursor);

      runInAction(() => {
        if (archived !== this.requestsArchived) return;
        if (refresh) {
          this.requests.clear();
        }
        response.items.forEach((req) => {
          this.requests.set(req.key, req);
        });
        this.requestsCursor = response.cursor;
        this.requestsHasMore = response.has_more;
        this.requestsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        if (archived !== this.requestsArchived) return;
        this.requestsLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch requests';
      });
    }
  }

  /**
   * Fetch a single request
   */
  async fetchRequest(requestKey: string): Promise<FHRequest | undefined> {
    try {
      const request = await requestApi.get(requestKey);
      runInAction(() => {
        // Store by the response's key (UUID)
        this.requests.set(request.key, request);
        // Also store by the key used to fetch (in case it was a datastore key)
        if (requestKey !== request.key) {
          this.requests.set(requestKey, request);
        }
      });
      return request;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch request';
      });
      return undefined;
    }
  }

  /**
   * Create a new request
   */
  async createRequest(params: CreateRequestParams): Promise<FHRequest | undefined> {
    runInAction(() => {
      this.error = null;
    });

    try {
      const request = await requestApi.create(params);
      runInAction(() => {
        this.requests.set(request.key, request);
      });
      return request;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to create request';
      });
      return undefined;
    }
  }

  /**
   * Sort messages chronologically (oldest first)
   */
  private sortMessages(messages: FHRequestMessage[]): FHRequestMessage[] {
    return [...messages].sort(
      (a, b) =>
        new Date(a.date_created).getTime() - new Date(b.date_created).getTime()
    );
  }

  /**
   * Fetch messages for a request
   */
  async fetchMessages(requestUuid: string, refresh = false): Promise<void> {
    if (this.messagesLoading) return;
    if (!refresh && this.messagesHasMore.get(requestUuid) === false) return;

    runInAction(() => {
      this.messagesLoading = true;
      this.error = null;
      if (refresh) {
        this.messagesCursors.set(requestUuid, null);
        this.messagesHasMore.set(requestUuid, true);
      }
    });

    try {
      const cursor = refresh
        ? undefined
        : this.messagesCursors.get(requestUuid) || undefined;
      const response = await requestApi.listMessages(requestUuid, cursor);

      runInAction(() => {
        const existing = refresh ? [] : this.messages.get(requestUuid) || [];
        const combined = [...existing, ...response.items];
        // Sort chronologically (oldest first) for chat-like display
        this.messages.set(requestUuid, this.sortMessages(combined));
        this.messagesCursors.set(requestUuid, response.cursor);
        this.messagesHasMore.set(requestUuid, response.has_more);
        this.messagesLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.messagesLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch messages';
      });
    }
  }

  /**
   * Send a message
   */
  async sendMessage(
    requestKey: string,
    content: string,
  ): Promise<FHRequestMessage | undefined> {
    try {
      const message = await requestApi.createMessage({
        request: requestKey,
        content,
      });

      // Get the request to find its key
      const request = this.requests.get(requestKey);
      if (request?.key) {
        runInAction(() => {
          const existing = this.messages.get(request.key) || [];
          this.messages.set(request.key, [...existing, message]);
        });
      }

      return message;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to send message';
      });
      return undefined;
    }
  }

  /**
   * Receive realtime update for a request
   */
  receiveRequestUpdate(request: FHRequest): void {
    console.log('[RequestStore] receiveRequestUpdate called with:', request.key, 'status:', request.status);
    console.log('[RequestStore] current keys in store:', Array.from(this.requests.keys()));
    runInAction(() => {
      // Store by all possible keys to ensure lookups work regardless of which key format is used
      this.requests.set(request.key, request);
      if (request.uuid && request.uuid !== request.key) {
        this.requests.set(request.uuid, request);
      }
      if (request.datastore_key && request.datastore_key !== request.key) {
        this.requests.set(request.datastore_key, request);
      }
      console.log('[RequestStore] after update, keys:', Array.from(this.requests.keys()));
    });
  }

  /**
   * Receive realtime update for a message
   */
  receiveMessageUpdate(requestUuid: string, message: FHRequestMessage): void {
    runInAction(() => {
      const existing = this.messages.get(requestUuid) || [];
      // Check if message already exists
      const existingIndex = existing.findIndex((m) => m.key === message.key);
      if (existingIndex >= 0) {
        existing[existingIndex] = message;
        this.messages.set(requestUuid, [...existing]);
      } else {
        // Add and sort to maintain chronological order
        this.messages.set(requestUuid, this.sortMessages([...existing, message]));
      }
    });
  }
}

export const requestStore = new RequestStore();
